import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { getControl, getGroup } from '@shared/helpers/form.helper';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { toNum } from '@shared/helpers/math.helper';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { formPaths } from '@shared/constants/form-paths.const';
import { getContribBiennaleControl } from '../validators/helpers/contrib-biennale-form.helpers';
import { cloneDeep } from 'lodash-es';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { addDays, format, isBefore, isValid, parseISO } from 'date-fns';
import { debounceTime, merge, startWith } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';

export const setContribBiennaleDatiSorgenteListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_Anno1__1(form, store);
    my_StPaPsAI(form, store);
    my_CoEcAtot(form, store);
    my_DataCostituz(form, store);
    calcolaSociInseritiListenerHelper(form, store);
};

const my_Anno1__1 = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_Anno1Control = getControl(form, `${formPaths.datiIdentificativi}.my_Anno1`);
    my_Anno1Control.valueChanges.pipe(withInitialValueDebounced(my_Anno1Control)).subscribe((my_Anno1: string) => {
        const anno1NumberValue = toNum(my_Anno1);
        const value = (anno1NumberValue - 1).toString(10);
        getContribBiennaleControl(form, 'my_Anno1__1').patchValue(value, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_ContributoBiennale.my_Anno1__1 = value;
            return myVerbaleClone;
        });
    });
};

const my_StPaPsAI = (form: FormGroup, store: Store<Revisione53State>): void => {
    const listen = (my_StPaPsAIPath: string, bilAnnoKey: 'my_BilAnno1' | 'my_BilAnno2'): void => {
        const my_StPaPsAIControl = getControl(form, `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.${my_StPaPsAIPath}`);
        my_StPaPsAIControl.valueChanges.pipe(withChangeDebounced(my_StPaPsAIControl)).subscribe((value) => {
            const my_CapitaleSocialeControl = getContribBiennaleControl(form, 'my_CapitaleSociale');
            const my_Anno1__1: string = getContribBiennaleControl(form, 'my_Anno1__1').value;
            const my_ContribPreCaricato: string = getContribBiennaleControl(form, 'my_ContribPreCaricato').value;
            const bilAnno: string = getControl(form, `${formPaths.datiBilancio}.${bilAnnoKey}`).value;

            if (bilAnno === my_Anno1__1 && my_ContribPreCaricato != '1') {
                my_CapitaleSocialeControl.patchValue(value, { emitEvent: false });
            }
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_ContributoBiennale.my_CapitaleSociale = my_CapitaleSocialeControl.value;
                return myVerbaleClone;
            });
        });
    };

    listen('my_StPaPsAI1', 'my_BilAnno1');
    listen('my_StPaPsAI2', 'my_BilAnno2');
};

const my_CoEcAtot = (form: FormGroup, store: Store<Revisione53State>): void => {
    const listen = (my_CoEcAtotPath: string, bilAnnoKey: 'my_BilAnno1' | 'my_BilAnno2', getValueToSet: (value: string) => string): void => {
        const my_CoEcAtotControl = getControl(form, `${formPaths.datiBilancio}.my_ContoEconomico.${my_CoEcAtotPath}`);

        my_CoEcAtotControl.valueChanges.pipe(withChangeDebounced(my_CoEcAtotControl)).subscribe((value: string) => {
            const my_RicaviVendPrestControl = getContribBiennaleControl(form, 'my_RicaviVendPrest');
            const my_Anno1__1: string = getContribBiennaleControl(form, 'my_Anno1__1').value;
            const my_ContribPreCaricato: string = getContribBiennaleControl(form, 'my_ContribPreCaricato').value;
            const bilAnno: string = getControl(form, `${formPaths.datiBilancio}.${bilAnnoKey}`).value;
            if (bilAnno === my_Anno1__1 && my_ContribPreCaricato != '1') {
                const valueToSet = toNum(getValueToSet(value));
                my_RicaviVendPrestControl.patchValue(valueToSet, { emitEvent: false });
            }

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_ContributoBiennale.my_RicaviVendPrest = my_RicaviVendPrestControl.value;
                return myVerbaleClone;
            });
        });
    };

    listen('my_CoEcAtot1', 'my_BilAnno1', (value) => value);
    listen('my_CoEcAtot2', 'my_BilAnno2', (value) => value);
};

const my_DataCostituz = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiIdGroup = getGroup(form, formPaths.datiIdentificativi);
    const my_DataCostituzControl = getControl(datiIdGroup, 'my_DataCostituz');
    my_DataCostituzControl.valueChanges.pipe(withChangeDebounced(my_DataCostituzControl)).subscribe((my_DataCostituz: string) => {
        const dataScadCalcPenalOrigValue = parseISO(getContribBiennaleControl(form, 'my_DataScadCalcPenalOrig').value);
        const my_Anno1 = getControl(datiIdGroup, 'my_Anno1')?.value;
        let newDataScadCalcPenal: Date = dataScadCalcPenalOrigValue;

        if (my_DataCostituz !== '') {
            const annoCostituz = new Date(my_DataCostituz).getFullYear().toString();
            const dataCostituzAdded = addDays(parseISO(my_DataCostituz), 90);
            if (annoCostituz >= my_Anno1 && isBefore(new Date(dataScadCalcPenalOrigValue), dataCostituzAdded)) {
                newDataScadCalcPenal = dataCostituzAdded;
            }
        }

        if (!isValid(newDataScadCalcPenal)) {
            return;
        }

        const formattedDataScadCalcPenal = format(newDataScadCalcPenal, 'yyyy-MM-dd');
        getContribBiennaleControl(form, 'my_DataScadCalcPenal').patchValue(formattedDataScadCalcPenal, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_ContributoBiennale.my_DataScadCalcPenal = formattedDataScadCalcPenal;
            return myVerbaleClone;
        });
    });
};

/**
 * Subscribes to soci primo fields, anno1, chiusura esercizio, and contribPreCaricato;
 * calculates my_SociInseriti and updates ContributoBiennale form control + store.
 * Condition: data1EsPrec === anno1 - 1, chiusura === '31-12', contribPreCaricato !== '1'.
 */
const calcolaSociInseritiListenerHelper = (form: FormGroup, store: Store<Revisione53State>): void => {
    const effGroup = getGroup(form, formPaths.baseSociale);
    const chiusuraControl = getControl(effGroup, 'my_ChiusuraEsercizio');
    const data1Control = getControl(effGroup, 'my_Data1EsPrec');
    const sociFisCoopControl = getControl(effGroup, 'my_SociFisCoopPrimo');
    const sociGiuCoopControl = getControl(effGroup, 'my_SociGiuCoopPrimo');
    const sociFisFinControl = getControl(effGroup, 'my_SociFisFinPrimo');
    const sociGiuFinControl = getControl(effGroup, 'my_SociGiuFinPrimo');
    const sociSpecControl = getControl(effGroup, 'my_SociSpecPrimo');
    const anno1Control = getControl(form, `${formPaths.datiIdentificativi}.my_Anno1`);
    const contribPreCaricatoControl = getContribBiennaleControl(form, 'my_ContribPreCaricato');
    const sociInseritiControl = getContribBiennaleControl(form, 'my_SociInseriti');

    merge(
        chiusuraControl.valueChanges.pipe(startWith(chiusuraControl.value)),
        data1Control.valueChanges.pipe(startWith(data1Control.value)),
        sociFisCoopControl.valueChanges.pipe(startWith(sociFisCoopControl.value)),
        sociGiuCoopControl.valueChanges.pipe(startWith(sociGiuCoopControl.value)),
        sociFisFinControl.valueChanges.pipe(startWith(sociFisFinControl.value)),
        sociGiuFinControl.valueChanges.pipe(startWith(sociGiuFinControl.value)),
        sociSpecControl.valueChanges.pipe(startWith(sociSpecControl.value)),
        anno1Control.valueChanges.pipe(startWith(anno1Control.value)),
        contribPreCaricatoControl.valueChanges.pipe(startWith(contribPreCaricatoControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const anno1 = toNum(anno1Control.value);
            const data1 = toNum(data1Control.value);

            if (data1 !== anno1 - 1 || chiusuraControl.value !== '31-12' || contribPreCaricatoControl.value === '1') {
                return;
            }

            const soci =
                toNum(sociFisCoopControl.value) + toNum(sociGiuCoopControl.value) + toNum(sociFisFinControl.value) + toNum(sociGiuFinControl.value) + toNum(sociSpecControl.value);

            sociInseritiControl.setValue(soci, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const clone: MyVerbale = cloneDeep(myVerbale);
                clone.my_SezioneI__Rilevazione.my_ContributoBiennale.my_SociInseriti = String(soci);
                return clone;
            });
        });
};
